import { logger } from '../../services/logger';
import React, { useState, useEffect } from 'react';
import { AlertCircle, ArrowUpRight, Info, Search, ChevronRight, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSettings } from '../../lib/settings/settingsContext';
import { BetTransactionService } from '../../services/BetTransactionService';
import { toast } from 'react-hot-toast';
import { formatDate } from '../../lib/utils';
import { PositionWithMarket as ConsolidatedPositionType, DetailedWalletTransaction } from '../../types';
import { ConfirmationModal } from '../common/ConfirmationModal';
import {
  formatMultiplier,
  formatSharePriceZar,
  formatSharesBare,
  formatSharesExact2,
} from '../../lib/displayFormat';
import {
  buildOutcomeTradeLedger,
  supplementLmsrLedgerWithPositionBuys,
  type OutcomeTradeLedgerEntry,
} from '../../lib/portfolioTradeLedger.ts';

interface GroupedMarketPosition {
  marketId: string;
  marketTitle: string;
  outcomes: {
    id: number;
    name: string;
    total_amount: number;
    count: number;
    status: 'active' | 'won' | 'lost' | 'cancelled';
    last_bet_date: string | undefined;
    potential_payout: number;
    positions: ConsolidatedPositionType[];
  }[];
  marketStatus: string;
}

interface PortfolioPositionsProps {
  positions: ConsolidatedPositionType[];
  transactions?: DetailedWalletTransaction[];
}

type PositionStatusTab = 'active' | 'settled' | 'all';

export const PortfolioPositions: React.FC<PortfolioPositionsProps> = ({
  positions: initialPositions,
  transactions: initialTransactions,
}) => {
  const { formatCurrency: formatCurrencySetting } = useSettings();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedMarkets, setExpandedMarkets] = useState<Record<string, boolean>>({});
  const [expandedOutcomes, setExpandedOutcomes] = useState<Record<string, boolean>>({});
  const [cancelingBetId, setCancelingBetId] = useState<string | null>(null);
  const [groupedPositions, setGroupedPositions] = useState<GroupedMarketPosition[]>([]);
  const [positions, setPositions] = useState<ConsolidatedPositionType[]>(initialPositions);
  const [transactions, setTransactions] = useState<DetailedWalletTransaction[]>(
    initialTransactions ?? [],
  );
  const [activeTab, setActiveTab] = useState<PositionStatusTab>('active');
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [positionToConfirm, setPositionToConfirm] = useState<ConsolidatedPositionType | null>(null);

  // Initialize positions from props
  useEffect(() => {
    setPositions(initialPositions);
  }, [initialPositions]);

  useEffect(() => {
    setTransactions(initialTransactions ?? []);
  }, [initialTransactions]);

  // Set up listener for portfolio updates
  useEffect(() => {
    const handlePortfolioUpdate = (event: CustomEvent<{ updatedPositions?: ConsolidatedPositionType[] }>) => {
      if (event.detail?.updatedPositions) {
        setPositions(event.detail.updatedPositions);
      }
    };

    window.addEventListener('portfolioUpdated', handlePortfolioUpdate as EventListener);

    return () => {
      window.removeEventListener('portfolioUpdated', handlePortfolioUpdate as EventListener);
    };
  }, []);

  // Group positions by market and outcome ID
  useEffect(() => {
    const groupPositionsByMarket = () => {
      // Filter positions
      const filteredPositions = positions.filter((pos) => {
        const tabMatch =
          activeTab === 'active'
            ? pos.status === 'active'
            : activeTab === 'settled'
              ? pos.status === 'won' || pos.status === 'lost' || pos.status === 'cancelled'
              : true;
        return (
          (pos.marketTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
            pos.option_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            pos.marketCategory.toLowerCase().includes(searchQuery.toLowerCase())) &&
          tabMatch
        );
      });

      // Group by market, then by option ID
      const marketsMap = new Map<string, GroupedMarketPosition>();
      
      filteredPositions.forEach(position => {
        if (!marketsMap.has(position.marketId)) {
          marketsMap.set(position.marketId, {
            marketId: position.marketId,
            marketTitle: position.marketTitle,
            outcomes: [],
            marketStatus: position.marketStatus || 'active'
          });
        }
        
        const market = marketsMap.get(position.marketId)!;
        
        // Find or create outcome group by option ID
        let outcomeGroup = market.outcomes.find(out => out.id === position.market_option_id); 
        if (!outcomeGroup) {
          outcomeGroup = {
            id: position.market_option_id,
            name: position.option_name,
            total_amount: 0,
            count: 0,
            status: 'active',
            last_bet_date: undefined,
            potential_payout: 0,
            positions: []
          };
          market.outcomes.push(outcomeGroup);
        }
        
        outcomeGroup.positions.push(position);
      });
      
      // Calculate stats for each outcome group
      marketsMap.forEach(market => {
        market.outcomes.forEach(outcomeGroup => {
          outcomeGroup.count = outcomeGroup.positions.length;
          outcomeGroup.total_amount = outcomeGroup.positions.reduce((sum, pos) => sum + pos.amountAfterFee, 0);
          
          outcomeGroup.potential_payout = outcomeGroup.positions.reduce((sum, pos) => 
             // Use currentPotentialWin for active positions
             sum + (pos.status === 'active' && typeof pos.currentPotentialWin === 'number' ? pos.currentPotentialWin : 0)
          , 0);
            
          if (outcomeGroup.positions.some(p => p.status === 'active')) {
            outcomeGroup.status = 'active';
          } else if (outcomeGroup.positions.every(p => p.status === 'cancelled')) {
            outcomeGroup.status = 'cancelled';
          } else if (outcomeGroup.positions.some(p => p.status === 'won')) {
             outcomeGroup.status = 'won'; 
          } else if (outcomeGroup.positions.some(p => p.status === 'lost')){
              outcomeGroup.status = 'lost';
          } else {
              outcomeGroup.status = outcomeGroup.positions[0]?.status || 'cancelled';
          }

          // Find latest bet date within the group
          outcomeGroup.last_bet_date = outcomeGroup.positions.reduce((latest, pos) => {
            // Use betTime instead of endDate
            const posDate = pos.betTime; 
            if (!latest) return posDate;
            if (!posDate) return latest;
            return new Date(posDate) > new Date(latest) ? posDate : latest;
          }, undefined as string | undefined);
        });
      });
      
      // Clean up: remove markets with no outcomes or empty outcomes
      const nonEmptyMarkets = Array.from(marketsMap.values())
        .filter(market => market.outcomes.length > 0);
      
      // Sort markets by title
      const sortedMarkets = nonEmptyMarkets
        .sort((a, b) => a.marketTitle.localeCompare(b.marketTitle));
      
      // Sort outcomes within each market by probability (highest first)
      sortedMarkets.forEach(market => {
        market.outcomes.sort((a, b) => {
          const aProb = a.positions[0]?.probability || 0;
          const bProb = b.positions[0]?.probability || 0;
          return bProb - aProb;
        });
      });
      
      setGroupedPositions(sortedMarkets);
    };
    
    groupPositionsByMarket();
  }, [positions, searchQuery, activeTab]);

  const toggleMarketExpansion = (marketId: string) => {
    setExpandedMarkets(prev => ({
      ...prev,
      [marketId]: !prev[marketId]
    }));
  };

  const toggleOutcomeExpansion = (outcomeId: string) => {
    setExpandedOutcomes(prev => ({
      ...prev,
      [outcomeId]: !prev[outcomeId]
    }));
  };

  // Cancel trade functionality disabled for users
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const requestCancelBet = (position: ConsolidatedPositionType) => {
    if (!canCancelPosition(position)) {
      toast.error('Cannot cancel trade: Trading likely ended.');
      return;
    }
    setPositionToConfirm(position);
    setIsConfirmOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (!positionToConfirm || !positionToConfirm.betId) return;

    const betId = positionToConfirm.betId;
    setCancelingBetId(betId);
    setIsConfirmOpen(false);

    try {
      const result = await BetTransactionService.cancelBet(betId);
      if (result) {
        toast.success('Trade cancelled successfully');
        const updatedPositions = positions.map(pos => {
          if (pos.betId === betId) {
            return { ...pos, status: 'cancelled' as const };
          }
          return pos;
        });
        setPositions(updatedPositions);
        window.dispatchEvent(new CustomEvent('betHistoryUpdated'));
        window.dispatchEvent(new CustomEvent('walletBalanceUpdated'));
      }
    } catch (error) {
      logger.error('Error cancelling trade:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to cancel trade');
    } finally {
      setCancelingBetId(null);
      setPositionToConfirm(null);
    }
  };

  const canCancelPosition = (position: ConsolidatedPositionType): boolean => {
    const now = new Date();

    // Basic status checks
    if (position.status !== 'active') return false;
    if (position.marketStatus !== 'active') return false;
    
    // Check market resolution time
    if (position.marketResolutionTime && new Date(position.marketResolutionTime) <= now) return false;

    // Check based on market type using data available in the position object
    if (position.marketType === 'fixed') {
      // For fixed markets, check the market's trading_end_time
      return !position.marketTradingEndTime || new Date(position.marketTradingEndTime) > now;
    } else if (position.marketType === 'knockout') {
      // Per-option / market trading_end_time (legacy); when null, only resolution_time applies above.
      const optionEndTimeValid = !position.optionTradingEndTime || new Date(position.optionTradingEndTime) > now;
      const marketEndTimeValid = !position.marketTradingEndTime || new Date(position.marketTradingEndTime) > now;
      return optionEndTimeValid && marketEndTimeValid;
    } 
    
    // Default to false if marketType is unknown or missing
    return false; 
  };

  const getStatusColor = (status?: string) => {
    if (status === 'won') return 'bg-green-100 text-green-700 border-green-200';
    if (status === 'lost') return 'bg-red-100 text-red-700 border-red-200';
    if (status === 'cancelled') return 'bg-gray-100 text-gray-600 border-gray-200';
    return 'bg-blue-100 text-blue-600 border-blue-200'; // Active
  };

  // Show all positions for an outcome, regardless of current tab filter
  const getAllPositionsForOutcome = (marketId: string, outcomeName: string) => {
    return positions.filter(position => 
      position.marketId === marketId && 
      position.option_name === outcomeName
    );
  };

  // Handle expanded outcome display with all positions for that outcome
  const renderOutcomePositions = (marketId: string, outcomeName: string) => {
    const allOutcomePositions = getAllPositionsForOutcome(marketId, outcomeName);
    const isLmsr = allOutcomePositions.some((p) => p.engine_type === 'lmsr');
    const betIdsForOutcome = new Set(
      allOutcomePositions.map((p) => String(p.betId)).filter((id) => id.length > 0),
    );

    const isGhostLmsrRow = (p: ConsolidatedPositionType): boolean => {
      if (p.engine_type !== 'lmsr') return false;
      const sh = Number(p.shares_owned ?? 0);
      if (sh > 1e-9) return false;
      const metrics = [
        Number(p.amountAfterFee ?? 0),
        Number(p.amount ?? 0),
        Number(p.lmsr_initial_stake_net ?? 0),
        Number(p.lmsr_initial_stake_gross ?? 0),
      ];
      return metrics.every((n) => Math.abs(n) < 1e-6);
    };

    const cardPositions = allOutcomePositions.filter((p) => !isGhostLmsrRow(p));

    let ledger: OutcomeTradeLedgerEntry[] = [];
    if (isLmsr) {
      const baseLedger = buildOutcomeTradeLedger(
        marketId,
        outcomeName,
        transactions,
        betIdsForOutcome,
      );
      ledger = supplementLmsrLedgerWithPositionBuys(baseLedger, allOutcomePositions, outcomeName);
    }

    const showWalletLedger = isLmsr && ledger.length > 0;
    const positionsForDetail = showWalletLedger
      ? cardPositions.filter((p) => {
          const sh = Number(p.shares_owned ?? 0);
          return Number.isFinite(sh) && sh > 1e-9;
        })
      : cardPositions;

    const ledgerBuyTotal = ledger
      .filter((e) => e.kind === 'buy')
      .reduce((s, e) => s + e.debitTotal, 0);
    const ledgerSellTotal = ledger
      .filter((e) => e.kind === 'sell')
      .reduce((s, e) => s + e.netCredit, 0);

    const renderPositionDetailCard = (position: ConsolidatedPositionType) => {
              const isLmsrSoldOut =
                position.engine_type === 'lmsr' && Number(position.shares_owned ?? 0) === 0;
              const portfolioStatusLabel = isLmsrSoldOut
                ? 'Closed'
                : (position.status || 'Active').charAt(0).toUpperCase() +
                  (position.status || 'Active').slice(1);

              const portfolioStatusClass =
                isLmsrSoldOut ?
                  'bg-gray-100 text-gray-700 border-gray-200'
                : getStatusColor(position.status);

              return (
              <div 
                key={position.betId || position.market_option_id} 
                className={`bg-white p-3 md:p-4 rounded border border-gray-200 ${
                  position.status === 'cancelled' ? 'opacity-75' : ''
                }`}
                {...(position.engine_type === 'lmsr'
                  ? {
                      'data-lmsr-net-after-fee': String(Number(position.amountAfterFee ?? position.amount ?? 0)),
                      'data-lmsr-shares': String(Number(position.shares_owned ?? 0)),
                    }
                  : {})}
              >
                <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
                  <div className="flex-grow">
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between gap-4 text-gray-700">
                        <span>Gross amount</span>
                        <span>{formatCurrencySetting(Number(position.amount ?? 0))}</span>
                      </div>
                      <div className="flex justify-between gap-4 text-gray-700">
                        <span>Fees &amp; spread</span>
                        <span>{formatCurrencySetting(Number(position.serviceFeeAmount ?? 0))}</span>
                      </div>
                      <div className="flex justify-between gap-4 font-medium text-gray-900">
                        <span>Net trade</span>
                        <span>{formatCurrencySetting(position.amountAfterFee)}</span>
                      </div>
                      {position.engine_type === 'lmsr' &&
                        position.lmsr_exit_net != null && (
                          <div className="flex justify-between gap-4 text-gray-700 pt-2 border-t border-gray-100">
                            <span>Sell credited (net)</span>
                            <span>{formatCurrencySetting(position.lmsr_exit_net)}</span>
                          </div>
                        )}
                      {position.engine_type === 'lmsr' &&
                        position.lmsr_exit_net != null &&
                        typeof position.realizedPnl === 'number' && (
                          <div className="flex justify-between gap-4 font-medium text-gray-900 pt-1">
                            <span>Round-trip realised P/L</span>
                            <span
                              className={
                                position.realizedPnl >= 0 ? 'text-green-600' : 'text-red-600'
                              }
                            >
                              {position.realizedPnl >= 0 ? '+' : '-'}
                              {formatCurrencySetting(Math.abs(position.realizedPnl))}
                            </span>
                          </div>
                        )}
                    </div>
                    {position.engine_type === 'lmsr' &&
                      position.status === 'active' &&
                      Number(position.shares_owned ?? 0) > 0 &&
                      position.current_market_value != null && (
                        <div className="mt-2 space-y-0.5 text-xs border-t border-gray-100 pt-2">
                          <div className="flex justify-between gap-4 text-gray-700">
                            <span>Current value</span>
                            <span className="font-medium">{formatCurrencySetting(position.current_market_value)}</span>
                          </div>
                          {position.current_price_per_share != null && (
                            <div className="flex justify-between gap-4 text-gray-500">
                              <span>Live price / share</span>
                              <span>R{formatSharePriceZar(position.current_price_per_share)}</span>
                            </div>
                          )}
                          <div className="flex justify-between gap-4">
                            <span className="text-gray-700">Live P/L</span>
                            <span
                              className={`font-semibold ${
                                (position.unrealizedPnl ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'
                              }`}
                            >
                              {(position.unrealizedPnl ?? 0) >= 0 ? '+' : '-'}
                              {formatCurrencySetting(Math.abs(position.unrealizedPnl ?? 0))}
                            </span>
                          </div>
                        </div>
                      )}
                    <div className="text-xs text-gray-500 mt-0.5">
                      {position.betTime ? formatDate(position.betTime) : 'N/A'}
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-3 md:mt-0">
                    <span className={`px-3 py-1.5 inline-flex text-sm leading-none font-medium rounded-full border ${portfolioStatusClass} justify-center sm:justify-start`}>
                      {portfolioStatusLabel}
                    </span>
                    
                    {position.status === 'active' && !isLmsrSoldOut && (
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                        <div className="text-right sm:text-left">
                          <div className="font-medium text-green-600">
                            {position.engine_type === 'lmsr'
                              ? `If you win: ${formatCurrencySetting(
                                  position.currentPotentialWin ?? Number(position.shares_owned ?? 0),
                                )}`
                              : `Potential: ${formatCurrencySetting(position.currentPotentialWin ?? 0)}`}
                          </div>
                          {position.engine_type === 'lmsr' ? (
                            <div className="text-xs text-gray-500 mt-0.5">
                              {position.shares_owned != null && Number(position.shares_owned) > 0
                                ? `Shares: ${formatSharesExact2(position.shares_owned)}`
                                : null}
                            </div>
                          ) : (
                            position.currentOdds !== undefined && (
                              <div className="text-xs text-gray-500 mt-0.5">
                                Odds:{' '}
                                <span className="font-medium text-gray-600">{formatMultiplier(position.currentOdds)}</span>
                              </div>
                            )
                          )}
                        </div>

                        {position.engine_type === 'lmsr' &&
                          position.marketStatus === 'active' &&
                          Number(position.shares_owned ?? 0) > 0 && (
                            <Link
                              to={`/markets/${position.marketId}?trade=sell&option=${position.market_option_id}`}
                              className="inline-flex items-center justify-center px-3 py-1.5 rounded-md text-sm font-medium bg-wayja-teal text-white hover:bg-wayja-dark-teal transition-colors"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Sell
                            </Link>
                          )}
                      </div>
                    )}
                    
                    <ArrowUpRight className="w-4 h-4 text-gray-400 hidden md:block self-center" />
                  </div>
                </div>
              </div>
            );
    };

    const nothingToShow = !showWalletLedger && cardPositions.length === 0;

    return (
      <div className="p-2 md:p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-1">Individual Trades</h4>
        {showWalletLedger && (
          <p className="text-xs text-gray-500 mb-3">
            Full trade history for this outcome (wallet debits for buys, net credits for sells), oldest first.
          </p>
        )}
        <div className="space-y-3">
          {nothingToShow ? (
            <div className="text-center py-3">
              <p className="text-sm text-gray-500">No positions found for this outcome.</p>
            </div>
          ) : (
            <>
              {showWalletLedger && (
                <div className="space-y-2">
                  {ledger.map((e) =>
                    e.kind === 'buy' ? (
                      <div
                        key={e.id}
                        className="bg-white p-3 md:p-4 rounded border border-gray-200 border-l-4 border-l-wayja-teal"
                      >
                        <div className="flex justify-between items-start gap-2">
                          <span className="text-sm font-semibold text-gray-900">Buy</span>
                          <span className="text-xs text-gray-500">{formatDate(e.at)}</span>
                        </div>
                        <div className="mt-2 flex justify-between gap-4 text-sm text-gray-700">
                          <span>
                            {e.kind === 'buy' && e.source === 'position'
                              ? 'Total paid (bet record)'
                              : 'Total paid (wallet debit)'}
                          </span>
                          <span className="font-medium">{formatCurrencySetting(e.debitTotal)}</span>
                        </div>
                        {e.kind === 'buy' && e.source === 'position' && (
                          <p className="text-[11px] text-gray-400 mt-1">
                            Shown from your bet row when the wallet line was not linked yet—refresh in a
                            minute or check Transactions.
                          </p>
                        )}
                      </div>
                    ) : (
                      <div
                        key={e.id}
                        className="bg-white p-3 md:p-4 rounded border border-gray-200 border-l-4 border-l-amber-500"
                      >
                        <div className="flex justify-between items-start gap-2">
                          <span className="text-sm font-semibold text-gray-900">Sell</span>
                          <span className="text-xs text-gray-500">{formatDate(e.at)}</span>
                        </div>
                        <div className="mt-2 flex justify-between gap-4 text-sm text-gray-700">
                          <span>Net received (after performance fee)</span>
                          <span className="font-medium">{formatCurrencySetting(e.netCredit)}</span>
                        </div>
                      </div>
                    )
                  )}
                  {ledger.some((l) => l.kind === 'buy') && ledger.some((l) => l.kind === 'sell') && (
                    <div className="flex justify-between gap-4 text-sm font-medium text-gray-900 px-1 pt-1">
                      <span>Net from listed buys &amp; sells</span>
                      <span
                        className={
                          ledgerSellTotal - ledgerBuyTotal >= 0 ? 'text-green-600' : 'text-red-600'
                        }
                      >
                        {ledgerSellTotal - ledgerBuyTotal >= 0 ? '+' : '-'}
                        {formatCurrencySetting(Math.abs(ledgerSellTotal - ledgerBuyTotal))}
                      </span>
                    </div>
                  )}
                </div>
              )}
              {showWalletLedger && positionsForDetail.length > 0 && (
                <p className="text-xs font-semibold text-gray-600 mt-2 pt-3 border-t border-gray-100">
                  Open holdings (live marks)
                </p>
              )}
              {positionsForDetail.map(renderPositionDetailCard)}
            </>
          )}
        </div>
      </div>
    );
  };

  // Render a single outcome row (potentially with all positions)
  const renderOutcome = (market: GroupedMarketPosition, outcome: { id: number; name: string; total_amount: number; count: number; status: string; last_bet_date: string | undefined; potential_payout: number; positions: ConsolidatedPositionType[] }) => {
    // Get active positions for this outcome
    const activeCount = outcome.positions.length;
    const isExpanded = expandedOutcomes[`${market.marketId}-${outcome.name}`] || false;

    // Level-1 aggregate stats for the total position on this outcome (Item 1):
    //   totalShares = sum of shares_owned
    //   wac         = SUM(price × qty) / SUM(qty) = SUM(amountAfterFee) / SUM(shares)
    //   currentPrice = latest current_price_per_share we have on any position
    //   unrealPL    = (currentPrice - wac) × totalShares
    const totalShares = outcome.positions.reduce(
      (s, p) => s + Number(p.shares_owned ?? 0),
      0,
    );
    const totalPaid = outcome.positions.reduce(
      (s, p) => s + Number(p.amountAfterFee ?? p.amount ?? 0),
      0,
    );
    const wac = totalShares > 0 ? totalPaid / totalShares : 0;
    const currentPrice =
      outcome.positions.find((p) => p.current_price_per_share != null)?.current_price_per_share ?? 0;
    const unrealPL = totalShares > 0 ? (currentPrice - wac) * totalShares : 0;
    const plSign = unrealPL >= 0 ? '+' : '-';
    const hasLmsrAggregates = totalShares > 0 && currentPrice > 0;

    return (
      <div key={outcome.name} className="mb-2 last:mb-0">
        <div 
          className={`flex flex-col md:grid md:grid-cols-[2fr,1fr,1fr,1fr,1fr,1fr] gap-2 md:gap-4 p-3 md:p-4 items-start md:items-center text-sm cursor-pointer 
                     ${isExpanded ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
          onClick={() => toggleOutcomeExpansion(`${market.marketId}-${outcome.name}`)}
        >
          {/* Outcome Name & Status Dot (Flex for mobile) */}
          <div className="flex flex-col gap-1 w-full">
            <div className="flex items-center gap-2 w-full justify-between md:justify-start">
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${getStatusColor(outcome.status)} flex-shrink-0`}></div>
                <span className="font-medium">{outcome.name}</span>
              </div>
              {/* Mobile only: Chevron */}
              <ChevronRight className={`h-4 w-4 md:hidden text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
            </div>
            {hasLmsrAggregates && (
              <div className="flex flex-col gap-0.5 text-xs text-gray-500 pl-5">
                <span>~{formatSharesBare(totalShares)} shares</span>
                <span>Avg buy R{formatSharePriceZar(wac)} per share</span>
                <span>Live price R{formatSharePriceZar(currentPrice)} per share</span>
                <span className={`font-semibold ${unrealPL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  Live P/L {plSign}
                  {formatCurrencySetting(Math.abs(unrealPL))}
                </span>
              </div>
            )}
          </div>
          
          {/* Details Section (Label-Value pairs for mobile) */}
          <div className="w-full md:w-auto">
            <span className="text-xs text-gray-500 md:hidden">Net Stake: </span>
            <span className="font-medium">{formatCurrencySetting(outcome.total_amount)}</span>
          </div>
          
          {/* Position Count */}
          <div className="w-full md:w-auto">
             <span className="text-xs text-gray-500 md:hidden">Positions: </span>
            <span className="flex items-center">
              <span>{activeCount}</span>
              {/* Desktop only: Chevron */}
              <ChevronRight className={`h-4 w-4 ml-1 text-gray-400 transition-transform hidden md:inline-block ${isExpanded ? 'rotate-90' : ''}`} />
            </span>
          </div>
          
          {/* Payout */}
          <div className="w-full md:w-auto">
            <span className="text-xs text-gray-500 md:hidden">Payout: </span>
            <span className="font-medium text-green-600">
              {formatCurrencySetting(outcome.potential_payout)}
            </span>
          </div>
          
          {/* Status */}
          <div className="w-full md:w-auto">
            <span className="text-xs text-gray-500 md:hidden">Status: </span>
            <span className={`px-2 py-1 inline-flex text-xs leading-none font-medium rounded-full ${getStatusColor(outcome.status)}`}>
              {(outcome.status || 'Active').charAt(0).toUpperCase() + (outcome.status || 'Active').slice(1)}
            </span>
          </div>
          
          {/* Last Update */}
          <div className="w-full md:w-auto text-xs text-gray-500">
            <span className="md:hidden">Last Trade: </span>
            {outcome.last_bet_date ? formatDate(outcome.last_bet_date) : 'N/A'}
          </div>
        </div>
        
        {isExpanded && renderOutcomePositions(market.marketId, outcome.name)}
      </div>
    );
  };

  // If there are no positions at all
  if (positions.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">Positions</h3>
        </div>
        <div className="p-8 text-center">
          <AlertCircle className="w-10 h-10 mx-auto mb-3 text-gray-400" />
          <p className="text-gray-500 mb-2">You don't have any positions yet.</p>
          <p className="text-gray-500">Place a trade on a market to see your positions here.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header Area */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Trades</h1>
          <p className="text-sm text-gray-500 mt-1">Manage and track all your active and settled trades</p>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search trades..."
              className="block w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <div className="flex border border-gray-200 rounded-lg overflow-hidden">
            {['active', 'settled', 'all'].map((tab) => (
            <button
                key={tab}
                className={`px-4 py-2 font-medium text-sm ${
                  activeTab === tab
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
                onClick={() => setActiveTab(tab as PositionStatusTab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Content Area */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6">
          {/* No positions state */}
          {groupedPositions.length === 0 && (
            <div className="text-center py-12">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100">
                <Info className="h-6 w-6 text-gray-400" />
        </div>
              <h3 className="mt-2 text-sm font-semibold text-gray-900">No trades found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {activeTab === 'active'
                  ? "You don't have any active trades yet."
                  : activeTab === 'settled'
                  ? "You don't have any settled trades yet."
                  : "You don't have any trades yet."}
              </p>
              <div className="mt-6">
                <a
                  href="/explore"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <ArrowUpRight className="-ml-1 mr-2 h-4 w-4" />
                  Explore Markets
                </a>
                          </div>
                        </div>
                      )}
          
          {/* Market Groups */}
          {groupedPositions.map((market) => (
            <div key={market.marketId} className="border-b border-gray-200 last:border-b-0">
              <div 
                className="py-3 px-2 md:px-0 flex flex-col md:flex-row items-start md:items-center justify-between cursor-pointer hover:bg-gray-50 rounded"
                onClick={() => toggleMarketExpansion(market.marketId)}
              >
                <div className="flex items-center mb-2 md:mb-0">
                  <ChevronRight 
                    className={`h-5 w-5 text-gray-400 mr-2 transition-transform ${expandedMarkets[market.marketId] ? 'transform rotate-90' : ''}`} 
                  />
                  {/* Allow market title to wrap and give it slightly more emphasis */}
                  <div className="flex items-center">
                    <h3 className="font-semibold text-gray-800 text-base leading-tight break-words mr-2">{market.marketTitle}</h3>
                    <Link 
                      to={`/markets/${market.marketId}`}
                      onClick={(e) => e.stopPropagation()}
                      className="text-blue-600 hover:text-blue-800 flex-shrink-0"
                      aria-label={`View market ${market.marketTitle}`}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
                <div className="text-sm text-gray-600 self-end md:self-center">
                  {market.outcomes.reduce((total, outcome) => total + outcome.count, 0)} 
                  {market.outcomes.reduce((total, outcome) => total + outcome.count, 0) === 1 ? ' position' : ' positions'}
                </div>
              </div>
              
              {expandedMarkets[market.marketId] && (
                <div className="pl-4 md:pl-6 pr-2 md:pr-4 py-2">
                  {market.outcomes.map((outcome) => renderOutcome(market, outcome))}
                  </div>
                )}
              </div>
          ))}
        </div>
      </div>

      {/* Render the Confirmation Modal INSIDE the main return */}
      <ConfirmationModal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirmCancel}
        title="Confirm Trade Cancellation"
        message={
          positionToConfirm ? (
            <div className="space-y-1 text-sm">
              <p>Are you sure you want to cancel your trade?</p>
              <p><strong>Market:</strong> {positionToConfirm.marketTitle}</p>
              <p><strong>Outcome:</strong> {positionToConfirm.option_name}</p>
              <p><strong>Trade Amount:</strong> {formatCurrencySetting(positionToConfirm.amount)}</p>
              <p><strong>Refund Amount (Net):</strong> {formatCurrencySetting(positionToConfirm.amountAfterFee)}</p>
            </div>
          ) : (
            'Loading trade details...'
          )
        }
        confirmText="Cancel Trade"
        cancelText="Keep Trade"
        isLoading={cancelingBetId === positionToConfirm?.betId}
        variant="danger"
      />
    </div>
  );
}; 